// Token search for the scenario / billing-code pickers (Trent, 2026-09-22).
//
// Both pickers used to match with a single contiguous `name.includes(query)`,
// which fails the way a reader actually types. "Hip MRI" found nothing — the
// one scenario carrying both words is "Hip Bursitis — Refractory with MRI and
// PRP Injection", where they sit 30 characters apart and in the other order.
// Across 12k scenarios that is the common case, not an edge case.
//
// So: split the query on whitespace and require EVERY token to appear
// somewhere in the record, in any order. Ranking still rewards a literal
// phrase hit and an early match, so exact typing stays at the top.
//
// NOTE this matches the words a record actually carries. It is not a synonym
// layer: a hip MRI is filed under CPT 73721 "MRI joint of lower extremity",
// which contains no "hip", and no amount of tokenising will find it. That
// needs an anatomy vocabulary — tracked separately.

/** Split a raw query into lowercased tokens. */
export function tokenize(q: string): string[] {
  return q.trim().toLowerCase().split(/\s+/).filter(Boolean);
}

/** Every token must appear somewhere in the (already lowercased) haystack. */
export function matchesTokens(haystack: string, tokens: string[]): boolean {
  for (const tok of tokens) if (!haystack.includes(tok)) return false;
  return true;
}

/**
 * Rank a hit: lower is better. `name` and `haystack` must already be
 * lowercased; `phrase` is the whole query, `tokens` its words.
 *
 *   0  name is exactly the query
 *   1  name starts with the whole query
 *   2  name contains the whole query
 *   3  every token is in the name
 *   4  every token is somewhere in the record (description, tags, category…)
 *  -1  no match
 */
export function rankMatch(name: string, haystack: string, phrase: string, tokens: string[]): number {
  if (!tokens.length) return -1;
  if (name === phrase) return 0;
  if (name.startsWith(phrase)) return 1;
  if (name.includes(phrase)) return 2;
  if (matchesTokens(name, tokens)) return 3;
  if (matchesTokens(haystack, tokens)) return 4;
  return -1;
}

/**
 * Where the match begins in the name, for tie-breaking within a rank — the
 * earliest token wins, so "MRI …" sorts above "… MRI".
 */
export function matchPosition(name: string, tokens: string[]): number {
  let best = Number.MAX_SAFE_INTEGER;
  for (const tok of tokens) {
    const i = name.indexOf(tok);
    if (i >= 0 && i < best) best = i;
  }
  return best === Number.MAX_SAFE_INTEGER ? 9999 : best;
}
